import {
    Button,
    Checkbox,
    Flex,
    pxToRem,
    useTheme,
} from '@archipro-design/aria';
import { useStyles } from '@archipro-design/aria';
import { Trash } from '@archipro-design/icons';
import { useDesignBoardPageState } from '../context/DesignBoardContext';
import type { DetailedPinWithChecked } from '../all-pins/AllPins';
import * as S from './BoardDetail.style';
import { useMatchesHandleData } from '~/modules/root';
import { useTracker } from '@archipro-website/tracker';
import BoardActionWithLoginPrompt from '@modules/design-board/component/board-detail/BoardActionWithLoginPrompt';

interface PinListSelectorProps {
    pins: DetailedPinWithChecked[];
    boardId?: number;
    onChange: (checked: boolean) => void;
    onDelete?: () => void;
}

const PinActionBar = ({
    pins,
    boardId,
    onChange,
    onDelete,
}: PinListSelectorProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    let noPins = true;
    let allSelected = true;
    const { state, dispatch } = useDesignBoardPageState();
    const tracker = useTracker();

    const canDelete = state.canEdit;
    const canMove = state.canEdit;

    const isAllPinsPage = useMatchesHandleData('isAllPinsPage', false);

    if (isAllPinsPage) return null;

    pins.forEach((p) => {
        if (p.checked) noPins = false;
        if (!p.checked) allSelected = false;
        if (allSelected && noPins) return;
    });

    const openMoveDrawer = () => {
        if (boardId === undefined) return;
        dispatch({
            type: 'OpenMovePinsDrawer',
            pinMoveData: {
                pinListString: pins.reduce((stringList, pin) => {
                    if (!pin.checked) return stringList;
                    return stringList === ''
                        ? `${pin.ID}`
                        : `${stringList}, ${pin.ID}`;
                }, ''),
                boardId,
            },
        });
    };

    const openCopyDrawer = () => {
        if (boardId === undefined) return;
        dispatch({
            type: 'OpenCopyPinsDrawer',
            pinMoveData: {
                pinListString: pins.reduce((stringList, pin) => {
                    if (!pin.checked) return stringList;
                    return stringList === ''
                        ? `${pin.ID}`
                        : `${stringList}, ${pin.ID}`;
                }, ''),
                boardId,
            },
        });
    };

    return (
        <Flex className={css(S.PinActionBar)} column={false}>
            <div className={css(S.CheckboxContainer)}>
                <Checkbox
                    indicator={{
                        kind: 'checkbox',
                        indicator: {
                            variables: {
                                iconSize: pxToRem(16),
                                border: `1px solid ${
                                    allSelected
                                        ? 'transparent'
                                        : theme.siteVariables.colors
                                              .charcoal[250]
                                }`,
                                backgroundColor: 'transparent',
                                size: pxToRem(22),
                                checkedBackgroundColor:
                                    theme.siteVariables.colors.gold['000'],
                            },
                        },
                    }}
                    label="Select all"
                    checked={allSelected}
                    onChange={(_, data) => {
                        onChange(data?.checked ?? false);
                    }}
                />
            </div>

            {!noPins && (
                <div className={css(S.ButtonDiv)}>
                    {canDelete && (
                        <Button
                            color="dark"
                            iconBefore={<Trash />}
                            size={24}
                            variables={{
                                buttonSize24IconXMargin: '0',
                                iconPadding: 0,
                            }}
                            variant="outlined"
                            onClick={() => {
                                const checkedPins = pins.filter(
                                    ({ checked }) => checked
                                );
                                tracker.log('PinMenuDelete', {
                                    label: checkedPins
                                        .map(
                                            (pin) =>
                                                pin.TileData
                                                    ?.professionalTitle || ''
                                        )
                                        .join(','),
                                    url: new URL(window.location.href),
                                    data: {
                                        ItemType: 'pin',
                                        ItemID: checkedPins.map(
                                            (pin) => pin.ID
                                        ),
                                    },
                                });
                                onDelete && onDelete();
                            }}
                        ></Button>
                    )}
                    {canMove && (
                        <BoardActionWithLoginPrompt
                            action={
                                <Button
                                    color="dark"
                                    size={24}
                                    variables={{
                                        buttonPadding: 16,
                                        iconPadding: 4,
                                    }}
                                    variant="outlined"
                                    onClick={() => openMoveDrawer()}
                                >
                                    MOVE
                                </Button>
                            }
                        />
                    )}
                    <BoardActionWithLoginPrompt
                        action={
                            <Button
                                color="dark"
                                size={24}
                                variables={{
                                    buttonPadding: 16,
                                    iconPadding: 4,
                                }}
                                variant="outlined"
                                onClick={() => openCopyDrawer()}
                            >
                                COPY
                            </Button>
                        }
                    />
                </div>
            )}
        </Flex>
    );
};

export default PinActionBar;
